// lib/use-feature-flags.hook.ts
import { useCallback, useEffect, useMemo, useState } from "react";

// lib/feature-flags.service.ts
var FeatureFlagsService = class {
  constructor(serverUrl) {
    this.serverUrl = serverUrl;
    this.getBy = this.getBy.bind(this);
  }
  getBy(appName) {
    const url = `${this.serverUrl}/feature-flags/${appName}`;
    return fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    }).then((response) => {
      if (response.ok) {
        return response.json();
      }
      throw new Error("Something went wrong");
    });
  }
};

// lib/use-feature-flags.hook.ts
function useFeatureFlags(options) {
  const { serverUrl, appName } = options;
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    const featureFlags = new FeatureFlagsService(serverUrl);
    featureFlags.getBy(appName).then((response) => response?.map(toExportedFeatureFlag) || []).then((list) => list.reduce(toFeatureFlagsHashMap, {})).then((map) => setData(map)).catch((err) => setError(err)).finally(() => setIsLoading(false));
  }, []);
  const state = useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
  const isEnabled = useCallback(
    (flagName) => {
      if (isLoading || error) return false;
      if (data[flagName] === void 0) return false;
      return data[flagName]?.isEnabled || false;
    },
    [data, error, isLoading]
  );
  return [state, isEnabled];
}
function toExportedFeatureFlag(featureFlag) {
  return {
    featureName: featureFlag.name,
    isEnabled: featureFlag.enabled
  };
}
function toFeatureFlagsHashMap(map = {}, exported) {
  return {
    ...map,
    [exported.featureName]: exported
  };
}

// lib/deprecated/with-feature-flags-provider.deprecated.tsx
import React2, { Component } from "react";

// lib/deprecated/feature-flags-context.deprecated.ts
import * as React from "react";
var FeatureFlagsContext = React.createContext({
  data: {},
  loading: false,
  error: null
});

// lib/deprecated/with-feature-flags-provider.deprecated.tsx
var withFeatureFlagsProvider = (Page, options) => {
  var _a;
  return _a = class extends Component {
    constructor() {
      super(...arguments);
      this.isComponentMounted = false;
      this.state = {
        error: null,
        data: {},
        loading: true
      };
    }
    async componentDidMount() {
      this.isComponentMounted = true;
      try {
        const featureFlagsService = new FeatureFlagsService(options.serverUrl);
        await featureFlagsService.getBy(options.appName).then((response) => {
          if (this.isComponentMounted) {
            this.setState({
              error: null,
              data: response,
              loading: false
            });
          }
        }).catch((error) => {
          if (this.isComponentMounted) {
            this.setState({
              error,
              data: {},
              loading: false
            });
          }
        });
      } catch (e) {
        if (this.isComponentMounted) {
          this.setState({
            error: "An unexpected error has occurred",
            data: {},
            loading: false
          });
        }
      }
    }
    componentWillUnmount() {
      this.isComponentMounted = false;
    }
    static async getInitialProps(ctx) {
      const pageProps = Page.getInitialProps && await Page.getInitialProps(ctx);
      return { ...pageProps };
    }
    render() {
      return /* @__PURE__ */ React2.createElement(FeatureFlagsContext.Provider, { value: this.state }, /* @__PURE__ */ React2.createElement(Page, { ...this.props }));
    }
  }, _a.displayName = `withFeatureFlagsProvider(${Page.displayName || Page.name || "Component"})`, _a;
};

// lib/deprecated/with-feature-flags.deprecated.tsx
import * as React3 from "react";
function withFeatureFlags(Component2) {
  function ComponentWithFeatureFlags(props) {
    return /* @__PURE__ */ React3.createElement(FeatureFlagsContext.Consumer, null, (contexts) => /* @__PURE__ */ React3.createElement(Component2, { ...props, ...contexts }));
  }
  const componentName = Component2.displayName || Component2.name || "Component";
  ComponentWithFeatureFlags.displayName = `withFeatureFlags(${componentName})`;
  return ComponentWithFeatureFlags;
}
export {
  useFeatureFlags,
  withFeatureFlags,
  withFeatureFlagsProvider
};
